import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  X,
  MapPin,
  Clock,
  DollarSign,
  Building,
  FileText,
  Camera,
  Upload,
  Trash2,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  Search, // Add Search icon
} from "lucide-react";
import api from "../../../utils/Api";

// InputField component
const InputField = ({ label, error, required, children }) => (
  <div className="space-y-2">
    {label && (
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    )}
    {children}
    {error && (
      <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
        <AlertCircle className="w-4 h-4" />
        {error}
      </p>
    )}
  </div>
);

const CreateMissionPage = () => {
  const { t, i18n } = useTranslation("createMission");
  const fileInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Job Title Autocomplete & Sector
  const [availableJobs, setAvailableJobs] = useState([]);
  const [availableSectors, setAvailableSectors] = useState([]);
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filteredJobs, setFilteredJobs] = useState([]);

  // States for wilayas and cities
  const [wilayas, setWilayas] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingWilayas, setLoadingWilayas] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    company: "",
    location: {
      line1: "",
      line2: "",
      zip_code: "",
      city: "",
      wilaya: "",
      coordinates: [0, 0],
    },
    salaryHour: "",
    schedule: [{ date: "", startTime: "", endTime: "" }],
    skills: [],
    requirements: "",
    educationLevel: "",
    imageFile: null,
    imagePreview: "",
    imageFile: null,
    imagePreview: "",
    status: "active",
    sector: "", // Add sector to formData
    customSector: "" // Add customSector to formData
  });

  const [errors, setErrors] = useState({});
  const [skillInput, setSkillInput] = useState("");

  const educationLevels = [
    "aucun",
    "CAP/BEP",
    "Bac",
    "Bac+2",
    "Bac+3",
    "Bac+5",
    "Doctorat",
  ];

  const steps = [
    { id: 1, name: t("add.steps.basic"), icon: Building },
    { id: 2, name: t("add.steps.location"), icon: MapPin },
    { id: 3, name: t("add.steps.schedule"), icon: Clock },
    { id: 4, name: t("add.steps.details"), icon: FileText },
  ];

  // Fetch wilayas and others on component mount
  useEffect(() => {
    fetchWilayas();
    fetchJobsAndSectors();
  }, []);

  const fetchWilayas = async () => {
    setLoadingWilayas(true);
    try {
      const response = await api.get("/data/wilayas");
      setWilayas(response.data);
    } catch (err) {
      console.error("Error fetching wilayas:", err);
    } finally {
      setLoadingWilayas(false);
    }
  };

  const fetchJobsAndSectors = async () => {
    try {
      const [jobsRes, sectorsRes] = await Promise.all([
        api.get("/job/"),
        api.get("/sector/")
      ]);
      setAvailableJobs(jobsRes.data);
      setAvailableSectors(sectorsRes.data);
    } catch (err) {
      console.error("Error fetching jobs/sectors:", err);
    }
  };

  // Filter jobs based on search term (formData.title)
  useEffect(() => {
    if (formData.title && !selectedJob) {
      const filtered = availableJobs.filter(job => {
        const jobTitle = job.title || '';
        return jobTitle.toLowerCase().includes(formData.title.toLowerCase());
      });
      setFilteredJobs(filtered.slice(0, 10));
    } else {
      setFilteredJobs([]);
    }
  }, [formData.title, availableJobs, selectedJob]);

  const handleSelectJob = (job) => {
    setSelectedJob(job);
    setFormData(prev => ({ ...prev, title: job.title, sector: '' })); // Keep title as string for Mission but reset sector
    setShowJobDropdown(false);
  };

  const fetchCities = async (wilayaCode) => {
    setLoadingCities(true);
    try {
      const response = await api.get(`/data/cities/wilaya/${wilayaCode}`);
      setCities(response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching cities:", err);
      return [];
    } finally {
      setLoadingCities(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.startsWith("location.")) {
      const locationField = field.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        location: { ...prev.location, [locationField]: value },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
      console.log(formData);
    }

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleWilayaChange = async (wilayaCode) => {
    handleInputChange("location.wilaya", wilayaCode);

    // Reset city and zip_code when wilaya changes
    handleInputChange("location.city", "");
    handleInputChange("location.zip_code", "");

    // Fetch cities for the selected wilaya
    if (wilayaCode) {
      await fetchCities(wilayaCode);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (cityName) => {
    const selectedCity = cities.find((city) => city.name === cityName);

    handleInputChange("location.city", cityName);
    handleInputChange("location.zip_code", selectedCity?.postalCode || formData.location.zip_code);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setErrors((prev) => ({
          ...prev,
          imageFile: t("validation.image.type"),
        }));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          imageFile: t("validation.image.size"),
        }));
        return;
      }

      const previewUrl = URL.createObjectURL(file);

      setFormData((prev) => ({
        ...prev,
        imageFile: file,
        imagePreview: previewUrl,
      }));

      setErrors((prev) => ({
        ...prev,
        imageFile: "",
      }));
    }
  };

  const removeImage = () => {
    if (formData.imagePreview) {
      URL.revokeObjectURL(formData.imagePreview);
    }

    setFormData((prev) => ({
      ...prev,
      imageFile: null,
      imagePreview: "",
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleScheduleChange = (index, field, value) => {
    const newSchedule = [...formData.schedule];
    newSchedule[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      schedule: newSchedule,
    }));
  };

  const addScheduleSlot = () => {
    setFormData((prev) => ({
      ...prev,
      schedule: [...prev.schedule, { date: "", startTime: "", endTime: "" }],
    }));
  };

  const removeScheduleSlot = (index) => {
    setFormData((prev) => ({
      ...prev,
      schedule: prev.schedule.filter((_, i) => i !== index),
    }));
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        skills: [...prev.skills, skillInput.trim()],
      }));
      setSkillInput("");
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((skill) => skill !== skillToRemove),
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.title.trim()) newErrors.title = t("validation.required");

      // Require sector if custom title (not matched with existing job - roughly)
      // Since Mission title is just a string, we check if it matches any existing job title perfectly.
      // If it doesn't match an existing job title, we consider it "new" and require sector.
      const exactMatch = availableJobs.find(job => job.title.toLowerCase() === formData.title.toLowerCase());
      if (!exactMatch && !formData.sector) {
        newErrors.sector = "Veuillez sélectionner un secteur pour ce nouveau type de mission";
      }

      if (!formData.description.trim())
        newErrors.description = t("validation.required");
    }

    if (step === 2) {
      if (!formData.location.wilaya.trim())
        newErrors["location.wilaya"] = t("validation.required");
      if (!formData.location.city.trim())
        newErrors["location.city"] = t("validation.required");
      if (!formData.location.zip_code.trim())
        newErrors["location.zip_code"] = t("validation.required");
      if (!formData.salaryHour || formData.salaryHour <= 0)
        newErrors.salaryHour = t("validation.required");
    }

    if (step === 3) {
      formData.schedule.forEach((slot, index) => {
        if (!slot.date)
          newErrors[`schedule.${index}.date`] = t("validation.required");
        if (!slot.startTime)
          newErrors[`schedule.${index}.startTime`] = t("validation.required");
        if (!slot.endTime)
          newErrors[`schedule.${index}.endTime`] = t("validation.required");
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key === "imageFile" || key === "imagePreview") return;

        if (key === "title" && selectedJob) {
          formDataToSend.append("title", selectedJob._id);
          return;
        }

        if (key === "location") {
          formDataToSend.append("location", JSON.stringify(formData.location));
        } else if (Array.isArray(formData[key])) {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else if (key === "sector" && formData.sector === "Autre") {
          formDataToSend.append("sector", formData.customSector);
        } else if (key === "customSector") {
          // Skip customSector as it's handled above
          return;
        } else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key].toString());
        }
      });

      // Ensure sector is sent if present
      // if (formData.sector) {
      //   formDataToSend.append('sector', formData.sector);
      // }

      if (formData.imageFile) {
        formDataToSend.append("image", formData.imageFile);
      }

      const response = await api.post("/mission/", formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("Mission created successfully:", response.data);
      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error creating mission:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Success Message */}
        {showSuccess && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3 animate-fade-in">
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="text-green-800 dark:text-green-200 font-medium">
              {t("add.success.message")}
            </p>
          </div>
        )}

        {/* Progress Stepper */}
        <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center gap-2 flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${isCompleted
                        ? "bg-primary-600 text-white"
                        : isActive
                          ? "bg-primary-600 text-white ring-4 ring-primary-100 dark:ring-primary-900"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                        }`}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        <Icon className="w-6 h-6" />
                      )}
                    </div>
                    <span
                      className={`text-sm font-medium text-center ${isActive || isCompleted
                        ? "text-gray-900 dark:text-white"
                        : "text-gray-500 dark:text-gray-400"
                        }`}
                    >
                      {step.name}
                    </span>
                  </div>

                  {index < steps.length - 1 && (
                    <div
                      className={`h-0.5 flex-1 mx-4 transition-colors duration-300 ${currentStep > step.id
                        ? "bg-primary-600"
                        : "bg-gray-200 dark:bg-gray-700"
                        }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <div className="p-6 sm:p-8">
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t("add.basic.title")}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("add.basic.subtitle")}
                  </p>
                </div>

                <InputField
                  label={t("add.title.label")}
                  error={errors.title}
                  required
                >
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => {
                        handleInputChange("title", e.target.value);
                        setSelectedJob(null); // Reset selection on manual typing
                        setShowJobDropdown(true);
                      }}
                      onFocus={() => setShowJobDropdown(true)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors ${errors.title
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300"
                        }`}
                      placeholder={t("add.title.placeholder")}
                    />
                    {/* Dropdown */}
                    {showJobDropdown && filteredJobs.length > 0 && !selectedJob && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {filteredJobs.map(job => (
                          <button
                            key={job._id}
                            type="button"
                            onClick={() => handleSelectJob(job)}
                            className="w-full px-4 py-3 text-left hover:bg-primary-50 dark:hover:bg-primary-900/20 transition flex items-center justify-between group"
                          >
                            <span className="text-gray-900 dark:text-white">
                              {job.title}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Click outside to close */}
                    {showJobDropdown && (
                      <div
                        className="fixed inset-0 z-0"
                        style={{ display: showJobDropdown ? 'block' : 'none', zIndex: -1 }} // Hack to put it behind input but above content? No, z-index needs to be handled carefully.
                      // Actually better to use a backdrop div
                      />
                    )}
                    {showJobDropdown && (
                      <div className="fixed inset-0 z-[5]" onClick={() => setShowJobDropdown(false)}></div>
                    )}
                  </div>

                    {/* Sector Selection (Conditional) */}
                    {formData.title && !availableJobs.some(j => j.title.toLowerCase() === formData.title.toLowerCase()) && (
                      <div className="mt-4 animate-fade-in space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Secteur d'activité (Nouveau type de mission) *
                          </label>
                          <select
                            value={availableSectors.some(s => s._id === formData.sector) ? formData.sector : (formData.sector ? 'Autre' : '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'Autre') {
                                handleInputChange("sector", 'Autre');
                              } else {
                                handleInputChange("sector", val);
                                handleInputChange("customSector", "");
                              }
                            }}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors ${errors.sector ? 'border-red-500' : 'border-gray-300'
                              }`}
                          >
                            <option value="">Sélectionnez un secteur</option>
                            {availableSectors.map(sector => (
                              <option key={sector._id} value={sector._id}>
                                {sector.name}
                              </option>
                            ))}
                            <option value="Autre">Autre</option>
                          </select>
                        </div>

                        {formData.sector === 'Autre' && (
                          <div className="animate-in slide-in-from-top-2 duration-200">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Précisez le secteur *
                            </label>
                            <input
                              type="text"
                              value={formData.customSector}
                              onChange={(e) => handleInputChange("customSector", e.target.value)}
                              placeholder="Tapez votre secteur"
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors ${errors.sector ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                          </div>
                        )}

                        {errors.sector && (
                          <p className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 mt-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.sector}
                          </p>
                        )}
                      </div>
                    )}
                </InputField>

                <InputField
                  label={t("add.description.label")}
                  error={errors.description}
                  required
                >
                  <textarea
                    rows={5}
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors resize-none ${errors.description
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300"
                      }`}
                    placeholder={t("add.description.placeholder")}
                  />
                </InputField>


              </div>
            )}

            {/* Step 2: Location & Salary */}
            {currentStep === 2 && (
              <div className="space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t("add.location.title")}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("add.location.subtitle")}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputField label={t("add.location.line1.label")}>
                    <input
                      type="text"
                      value={formData.location.line1}
                      onChange={(e) =>
                        handleInputChange("location.line1", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder={t("add.location.line1.placeholder")}
                    />
                  </InputField>

                  <InputField label={t("add.location.line2.label")}>
                    <input
                      type="text"
                      value={formData.location.line2}
                      onChange={(e) =>
                        handleInputChange("location.line2", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder={t("add.location.line2.placeholder")}
                    />
                  </InputField>

                  {/* Wilaya Select */}
                  <div className="md:col-span-2">
                    <InputField
                      label={t("add.location.wilaya.label")}
                      error={errors["location.wilaya"]}
                      required
                    >
                      <select
                        value={formData.location.wilaya}
                        onChange={(e) => handleWilayaChange(e.target.value)}
                        disabled={loadingWilayas}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed ${errors["location.wilaya"]
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300"
                          }`}
                      >
                        <option value="">
                          {loadingWilayas
                            ? "Chargement..."
                            : t("add.location.wilaya.placeholder")}
                        </option>
                        {wilayas?.map((wilaya) => (
                          <option key={wilaya.code} value={wilaya.code}>
                            {wilaya.code +
                              " - " +
                              (wilaya[`name_${i18n.language}`] || wilaya.name_fr)}
                          </option>
                        ))}
                      </select>
                    </InputField>
                  </div>

                  {/* City Select */}
                  <InputField
                    label={t("add.location.city.label")}
                    error={errors["location.city"]}
                    required
                  >
                    <input
                      type="text"
                      list="mission-cities-list"
                      value={formData.location.city}
                      onChange={(e) => handleCityChange(e.target.value)}
                      disabled={!formData.location.wilaya || loadingCities}
                      placeholder={
                        !formData.location.wilaya
                          ? "Sélectionnez d'abord une wilaya"
                          : loadingCities
                            ? "Chargement..."
                            : t("add.location.city.placeholder")
                      }
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed ${errors["location.city"]
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300"
                        }`}
                    />
                    <datalist id="mission-cities-list">
                      {cities?.map((city) => (
                        <option key={city.id} value={city.name} />
                      ))}
                    </datalist>
                  </InputField>

                  {/* Zip Code - Auto-filled */}
                  <InputField
                    label={t("add.location.zipCode.label")}
                    error={errors["location.zip_code"]}
                    required
                  >
                    <input
                      type="text"
                      value={formData.location.zip_code}
                      onChange={(e) => handleInputChange("location.zip_code", e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors ${errors["location.zip_code"]
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300"
                        }`}
                      placeholder={t("add.location.zipCode.placeholder")}
                    />
                  </InputField>
                </div>

                <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3 mb-6">
                    <DollarSign className="w-6 h-6 text-primary-600" />
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {t("add.salary.title")}
                    </h3>
                  </div>

                  <div className="max-w-md">
                    <InputField
                      label={t("add.salary.hourly")}
                      error={errors.salaryHour}
                      required
                    >
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.salaryHour}
                          onChange={(e) =>
                            handleInputChange(
                              "salaryHour",
                              parseFloat(e.target.value)
                            )
                          }
                          className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors ${errors.salaryHour
                            ? "border-red-500 focus:ring-red-500"
                            : "border-gray-300"
                            }`}
                          placeholder="450.00"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium">
                          DA
                        </span>
                      </div>
                    </InputField>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Schedule */}
            {currentStep === 3 && (
              <div className="space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t("add.schedule.title")}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("add.schedule.subtitle")}
                  </p>
                </div>

                <div className="space-y-4">
                  {formData.schedule.map((slot, index) => (
                    <div
                      key={index}
                      className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:border-primary-300 dark:hover:border-primary-700 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                            {t("add.schedule.slot")} {index + 1}
                          </h3>
                        </div>
                        {formData.schedule.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeScheduleSlot(index)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <InputField
                          label={t("add.schedule.date")}
                          error={errors[`schedule.${index}.date`]}
                          required
                        >
                          <input
                            type="date"
                            value={slot.date}
                            onChange={(e) =>
                              handleScheduleChange(index, "date", e.target.value)
                            }
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white transition-colors ${errors[`schedule.${index}.date`]
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                              }`}
                          />
                        </InputField>

                        <InputField
                          label={t("add.schedule.startTime")}
                          error={errors[`schedule.${index}.startTime`]}
                          required
                        >
                          <input
                            type="time"
                            value={slot.startTime}
                            onChange={(e) =>
                              handleScheduleChange(
                                index,
                                "startTime",
                                e.target.value
                              )
                            }
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white transition-colors ${errors[`schedule.${index}.startTime`]
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                              }`}
                          />
                        </InputField>

                        <InputField
                          label={t("add.schedule.endTime")}
                          error={errors[`schedule.${index}.endTime`]}
                          required
                        >
                          <input
                            type="time"
                            value={slot.endTime}
                            onChange={(e) =>
                              handleScheduleChange(index, "endTime", e.target.value)
                            }
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white transition-colors ${errors[`schedule.${index}.endTime`]
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                              }`}
                          />
                        </InputField>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addScheduleSlot}
                    className="flex items-center justify-center w-full py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-primary-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:border-primary-400 dark:hover:text-primary-400 dark:hover:bg-primary-900/20 transition-all font-medium"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    {t("add.schedule.add")}
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Skills & Requirements */}
            {currentStep === 4 && (
              <div className="space-y-6 animate-fade-in">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {t("add.skills.title")}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("add.skills.placeholder")}
                  </p>
                </div>

                {/* Skills */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t("add.skills.title")}
                  </label>

                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && (e.preventDefault(), addSkill())
                      }
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder={t("add.skills.placeholder")}
                    />
                    <button
                      type="button"
                      onClick={addSkill}
                      className="px-5 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-5 h-5" />
                      {t("add.skills.add")}
                    </button>
                  </div>

                  {formData.skills.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      {formData.skills.map((skill, index) => (
                        <span
                          key={index}
                          style={{
                            backgroundColor: "rgba(37, 99, 235, 0.1)",
                            color: "rgb(37, 99, 235)",
                          }}
                          className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium dark:bg-primary-900/40 dark:text-primary-300"
                        >
                          {skill}
                          <button
                            type="button"
                            onClick={() => removeSkill(skill)}
                            className="ml-2 hover:bg-primary-200 dark:hover:bg-primary-800 rounded p-0.5 transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Requirements & Education */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <InputField label={t("add.requirements.description")}>
                    <textarea
                      rows={6}
                      value={formData.requirements}
                      onChange={(e) =>
                        handleInputChange("requirements", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors resize-none"
                      placeholder={t("add.requirements.placeholder")}
                    />
                  </InputField>

                  <InputField label={t("add.education.level")}>
                    <select
                      value={formData.educationLevel}
                      onChange={(e) =>
                        handleInputChange("educationLevel", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                    >
                      <option value="">{t("add.education.select")}</option>
                      {educationLevels.map((level) => (
                        <option key={level} value={level}>
                          {t(`add.education.levels.${level}`)}
                        </option>
                      ))}
                    </select>
                  </InputField>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between gap-4 pt-8 mt-8 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-6 py-3 border rounded-lg font-medium transition-all ${currentStep === 1
                  ? "border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                  : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
              >
                <ChevronLeft className="w-5 h-5" />
                {t("add.actions.previous")}
              </button>

              <div className="flex gap-3">
                {currentStep === steps.length ? (
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t("add.actions.publishing")}
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        {t("add.actions.publish")}
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="flex items-center gap-2 px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all font-medium"
                  >
                    {t("add.actions.next")}
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CreateMissionPage;
